// Centralized SEO copy for every route (DE + EN).
// Source of truth: docs/seo-meta-tags.md.
// Update text here; routes consume via pageHead() in src/lib/seo.ts.

import { SITE_URL } from "./locale";

export type PageSeo = {
  title: string;
  description: string;
};

export type LocalizedSeo = {
  de: PageSeo & { path: string };
  en: PageSeo & { path: string };
  noindex?: boolean;
};

export const pages = {
  home: {
    de: {
      path: "/",
      title: "Calvarius — Online-Marketing wirtschaftlich gedacht",
      description:
        "Digitalagentur seit 2013. Performance Marketing, SEO und eigene Tools für E-Commerce und SaaS. Wirtschaftlich gedacht, plattformkritisch, messbar.",
    },
    en: {
      path: "/en",
      title: "Calvarius — Online Marketing, Economically Considered",
      description:
        "Digital agency since 2013. Performance marketing, SEO, and proprietary tools for e-commerce and SaaS. Economically driven, platform-critical, measurable.",
    },
  },
  services: {
    de: {
      path: "/leistungen",
      title: "Leistungen — Performance, SEO, Tools — Calvarius",
      description:
        "Performance Marketing, SEO, Conversion-Optimierung, Tracking, Strategie und Web-Entwicklung. Plus drei eigene Tools für Feeds, Creatives und Meta-Ads.",
    },
    en: {
      path: "/en/services",
      title: "Services — Performance, SEO, Tools — Calvarius",
      description:
        "Performance marketing, SEO, conversion optimization, tracking, strategy, and web development. Plus three proprietary tools for feeds, creatives, Meta ads.",
    },
  },
  about: {
    de: {
      path: "/ueber-uns",
      title: "Über Calvarius — Digitalagentur seit 2013",
      description:
        "Calvarius ist eine inhabergeführte Digitalagentur aus Karlstadt am Main. Operative Betreuung durch den Geschäftsführer André Freier. Unabhängig, klar.",
    },
    en: {
      path: "/en/about",
      title: "About Calvarius — Digital Agency Since 2013",
      description:
        "Calvarius is an owner-led digital agency from Karlstadt am Main, Germany. Operational care directly by founder André Freier. Independent, plain-spoken.",
    },
  },
  contact: {
    de: {
      path: "/kontakt",
      title: "Kontakt — Erst einordnen, dann entscheiden — Calvarius",
      description:
        "Ein erstes Gespräch dient bei uns nicht dem Verkauf, sondern der Einordnung. Schreiben Sie uns kurz Ihre Situation — wir antworten binnen 48 Stunden.",
    },
    en: {
      path: "/en/contact",
      title: "Contact — Assessment Before Decisions — Calvarius",
      description:
        "A first conversation isn't a sales pitch, it's an assessment. Briefly describe your situation — we typically reply within 48 hours, business days.",
    },
  },
  performanceMarketing: {
    de: {
      path: "/leistungen/performance-marketing",
      title: "Performance Marketing — Wirtschaftlich gedacht — Calvarius",
      description:
        "Google Ads, Meta, TikTok, LinkedIn — gesteuert auf Wirtschaftlichkeit, nicht auf Plattformlogik. Mit eigenem Feed-, Creative- und Kampagnen-Toolset.",
    },
    en: {
      path: "/en/services/performance-marketing",
      title: "Performance Marketing — Economically Driven — Calvarius",
      description:
        "Google Ads, Meta, TikTok, LinkedIn — managed for profitability, not platform logic. With proprietary feed, creative, and campaign tooling.",
    },
  },
  seoAiSeo: {
    de: {
      path: "/leistungen/seo-ai-seo",
      title: "SEO & AI SEO — Sichtbarkeit als Hebel — Calvarius",
      description:
        "Klassisches SEO plus parallele Optimierung für ChatGPT, Perplexity und Claude. Nicht alle Sichtbarkeit ist wertvoll — wir investieren wo es wirkt.",
    },
    en: {
      path: "/en/services/seo-ai-seo",
      title: "SEO & AI SEO — Visibility as Economic Lever — Calvarius",
      description:
        "Classical SEO plus parallel optimization for ChatGPT, Perplexity, and Claude. Not all visibility is valuable — we invest only where it has impact.",
    },
  },
  geoGenerativeEngine: {
    de: {
      path: "/leistungen/geo-generative-engine-optimization",
      title: "Generative Engine Optimization (GEO) — Sichtbar in KI-Antworten — Calvarius",
      description:
        "Sichtbar in ChatGPT, Perplexity und Google AI Overviews — aber nur dort, wo KI-Sichtbarkeit wirtschaftlich zählt. GEO ohne Garantieversprechen, mit ehrlicher Share-of-Answer-Messung.",
    },
    en: {
      path: "/en/services/geo-generative-engine-optimization",
      title: "Generative Engine Optimization (GEO) — Visible in AI Answers — Calvarius",
      description:
        "Visible in ChatGPT, Perplexity and Google AI Overviews — only where AI visibility matters economically. GEO without guarantees, with honest share-of-answer measurement.",
    },
  },
  conversionWebOptimization: {
    de: {
      path: "/leistungen/conversion-web-optimierung",
      title: "Conversion- & Web-Optimierung — Mehr aus Traffic — Calvarius",
      description:
        "Funnel-Analyse, Argumentationslogik, technische Performance. Wir optimieren dort, wo messbar Geld verloren geht — nicht überall, sondern gezielt.",
    },
    en: {
      path: "/en/services/conversion-optimization",
      title: "Conversion & Web Optimization — More From Traffic — Calvarius",
      description:
        "Funnel analysis, argumentation logic, technical performance. We optimize where measurable money is being lost — targeted, not everywhere at once.",
    },
  },
  conversionRateOptimization: {
    de: {
      path: "/leistungen/conversion-rate-optimierung",
      title: "Conversion-Rate-Optimierung Agentur — Hypothesen-Disziplin — Calvarius",
      description:
        "CRO-Agentur mit Forschungs-Disziplin: quantitative Funnel-Analyse, qualitative Insights, statistisch saubere Tests. Für E-Commerce, B2B und SaaS.",
    },
    en: {
      path: "/en/services/conversion-rate-optimization",
      title: "Conversion Rate Optimization Agency — Hypothesis Discipline — Calvarius",
      description:
        "CRO agency with research discipline: quantitative funnel analysis, qualitative insights, statistically clean tests. For e-commerce, B2B, and SaaS.",
    },
  },
  trackingDataFoundation: {
    de: {
      path: "/leistungen/tracking-datenbasis",
      title: "Tracking & Datenbasis — Belastbare Entscheidungen — Calvarius",
      description:
        "Server-side Tracking, Enhanced Conversions, GA4, Consent-Management. Daten ohne Einordnung sind gefährlich. Wir bauen die Basis für saubere Steuerung.",
    },
    en: {
      path: "/en/services/tracking-data-foundation",
      title: "Tracking & Data Foundation — Reliable Decisions — Calvarius",
      description:
        "Server-side tracking, Enhanced Conversions, GA4, consent management. Data without context is dangerous. We build the foundation for clean control.",
    },
  },
  strategicCoordination: {
    de: {
      path: "/leistungen/strategie-koordination",
      title: "Strategisches Sparring & Koordination — Calvarius",
      description:
        "Unabhängige Einordnung für Geschäftsführung und Marketing-Leitung. Sparring auf Augenhöhe, Koordination interner Teams und externer Dienstleister.",
    },
    en: {
      path: "/en/services/strategic-coordination",
      title: "Strategic Sparring & Coordination — Calvarius",
      description:
        "Independent assessment for management and marketing leadership. Eye-level sparring, coordination of internal teams and external service providers.",
    },
  },
  digitalDevelopment: {
    de: {
      path: "/leistungen/entwicklung-digitaler-dienste",
      title: "Entwicklung digitaler Dienste — PWA, Native & Custom-SaaS — Calvarius",
      description:
        "Individuelle digitale Dienste auf React-Basis: Progressive Web Apps, native Apps via Wrapping und maßgeschneidertes SaaS. Infrastruktur statt Wegwerf-Projekt.",
    },
    en: {
      path: "/en/services/digital-development",
      title: "Digital Services Development — PWA, Native & Custom SaaS — Calvarius",
      description:
        "Custom digital services on a React foundation: Progressive Web Apps, native apps via wrapping, and tailored SaaS. Infrastructure, not a throwaway project.",
    },
  },
  websiteDevelopment: {
    de: {
      path: "/leistungen/website-entwicklung",
      title: "Website-Entwicklung — Performance-Asset, kein Plug-in-Friedhof — Calvarius",
      description:
        "Schnelle, SEO-stabile Websites — klassisch (WordPress, TYPO3) oder headless mit Sanity. Content-getrieben, wartbar, als Performance-Asset gebaut.",
    },
    en: {
      path: "/en/services/website-development",
      title: "Website Development — A Performance Asset — Calvarius",
      description:
        "Fast, SEO-stable websites — classic (WordPress, TYPO3) or headless with Sanity. Content-driven, maintainable, built as a performance asset.",
    },
  },
  shopEntwicklung: {
    de: {
      path: "/leistungen/onlineshop-entwicklung",
      title: "Onlineshop-Entwicklung — Verkaufende Systeme statt Bezahlbutton — Calvarius",
      description:
        "Onlineshops auf Shopware, Shopify und WooCommerce. Checkout, Payment, Produktdaten und Schnittstellen sauber gedacht — als verkaufendes System, nicht zusammengesteckt.",
    },
    en: {
      path: "/en/services/ecommerce-development",
      title: "E-Commerce Development — Selling Systems, Not a Pay Button — Calvarius",
      description:
        "Online shops on Shopware, Shopify, and WooCommerce. Checkout, payment, product data, and interfaces designed as a selling system — not bolted together.",
    },
  },
  shopifyAgentur: {
    de: {
      path: "/leistungen/onlineshop-entwicklung/shopify-agentur",
      title: "Shopify-Agentur — Shops entwickeln, migrieren, betreuen — Calvarius",
      description:
        "Shopify-Agentur für Händler, die verkaufen wollen: Shopify-Shop erstellen lassen, Migration auf Shopify, Betreuung & Schnittstellen. Ehrlich beraten, sauber gebaut.",
    },
    en: {
      path: "/en/services/ecommerce-development/shopify-agency",
      title: "Shopify Agency — Build, Migrate, Maintain Stores — Calvarius",
      description:
        "Shopify agency for merchants who want to sell: build a Shopify store, migrate to Shopify, ongoing care and integrations. Honest advice, cleanly engineered.",
    },
  },
  tools: {
    de: {
      path: "/leistungen/tools",
      title: "Tools — Eigene Software für Performance-Marketing — Calvarius",
      description:
        "Drei eigene Tools im produktiven Einsatz: Delta für Feeds, picazzoo für Creatives, GonzalAds für Meta-Kampagnen. Plus KI-beschleunigte Custom-SaaS.",
    },
    en: {
      path: "/en/services/tools",
      title: "Tools — Proprietary Software for Performance — Calvarius",
      description:
        "Three proprietary tools in productive use: Delta for feeds, picazzoo for creatives, GonzalAds for Meta campaigns. Plus AI-accelerated custom SaaS.",
    },
  },
  delta: {
    de: {
      path: "/leistungen/delta",
      title: "Delta — Produktfeed-Management für E-Commerce — Calvarius",
      description:
        "Multi-Source, Multi-Target, regelbasierte Optimierung. Delta liefert plattformfertige Feeds an Google Shopping, Meta, Amazon, eBay — vollautomatisch.",
    },
    en: {
      path: "/en/services/delta",
      title: "Delta — Product Feed Management for E-Commerce — Calvarius",
      description:
        "Multi-source, multi-target, rule-based optimization. Delta delivers platform-ready feeds to Google Shopping, Meta, Amazon, eBay — fully automated.",
    },
  },
  picazzoo: {
    de: {
      path: "/leistungen/picazzoo",
      title: "picazzoo — KI-Creatives für E-Commerce-Marken — Calvarius",
      description:
        "Produktbilder, Werbevideos und Banner in Minuten statt Wochen. KI-getrieben, markentreu, mit Brand-DNA-Injection. Verfügbar unter picazzoo.com.",
    },
    en: {
      path: "/en/services/picazzoo",
      title: "picazzoo — AI Creatives for E-Commerce Brands — Calvarius",
      description:
        "Product images, video ads, and banners in minutes instead of weeks. AI-driven, brand-true, with brand-DNA injection. Available at picazzoo.com.",
    },
  },
  gonzalads: {
    de: {
      path: "/leistungen/gonzalads",
      title: "GonzalAds — Meta Ads. 80 % schneller — Calvarius",
      description:
        "KI-Wizard, Bulk-Engine, Automation Rules — endlich Kontrolle über Meta-Kampagnen. 80–90 % Zeitersparnis, null Placement-Fehler. Test auf gonzalads.com.",
    },
    en: {
      path: "/en/services/gonzalads",
      title: "GonzalAds — Meta Ads. 80% Faster — Calvarius",
      description:
        "AI wizard, bulk engine, automation rules — finally control over Meta campaigns. 80–90% time savings, zero placement errors. Test at gonzalads.com.",
    },
  },
  pmGoogleAds: {
    de: {
      path: "/leistungen/performance-marketing/google-ads",
      title: "Google Ads Agentur — Wirtschaftlich gesteuert seit 2013 — Calvarius",
      description:
        "Google Ads Agentur für E-Commerce, B2B und SaaS. Wirtschaftlich gesteuert, plattformkritisch, mit eigenem Feed-Tool. Audit in 30 Minuten — kostenlos.",
    },
    en: {
      path: "/en/services/performance-marketing/google-ads",
      title: "Google Ads Agency — Economically Managed Since 2013 — Calvarius",
      description:
        "Google Ads agency for e-commerce, B2B, and SaaS. Economically managed, platform-critical, with proprietary feed tooling. Free 30-minute audit available.",
    },
  },
  pmMetaAds: {
    de: {
      path: "/leistungen/performance-marketing/meta-ads",
      title: "Meta Ads Agentur — Performance ohne Plattformlogik seit 2013 — Calvarius",
      description:
        "Meta Ads Agentur für E-Commerce, B2C-SaaS und B2B mit visuellem Hebel. Creative-Disziplin, server-side Tracking, eigenes Bulk-Tool. Audit in 30 Minuten — kostenlos.",
    },
    en: {
      path: "/en/services/performance-marketing/meta-ads",
      title: "Meta Ads Agency — Performance Beyond Platform Logic Since 2013 — Calvarius",
      description:
        "Meta Ads agency for e-commerce, B2C SaaS, and B2B with visual leverage. Creative discipline, server-side tracking, proprietary bulk tooling. Free 30-minute audit.",
    },
  },
  pmMetaAdsRecruiting: {
    de: {
      path: "/leistungen/performance-marketing/meta-ads/recruiting",
      title: "Meta Ads Recruiting — Mitarbeiter-Gewinnung mit Targeting-Disziplin — Calvarius",
      description:
        "Meta Ads für Mitarbeiter-Gewinnung in Pflege, Handwerk, Logistik, Produktion, Gastronomie. 60–90 % niedrigere CPA als klassische Stellen-Portale. Ab €800/Monat.",
    },
    en: {
      path: "/en/services/performance-marketing/meta-ads/recruiting",
      title: "Meta Ads Recruiting — Employee Acquisition with Targeting Discipline — Calvarius",
      description:
        "Meta Ads for employee recruitment in care, trades, logistics, manufacturing, hospitality. 60–90% lower CPA than classical job portals. From €800/month.",
    },
  },
  pmSocialMediaWerbung: {
    de: {
      path: "/leistungen/performance-marketing/social-media-werbung",
      title: "Social Media Werbung — Paid Social für Performance — Calvarius",
      description:
        "Paid Social Media Marketing für Meta, LinkedIn, TikTok aus Karlstadt am Main. Performance-Disziplin, kein Content-Spielplatz. Plattform-kritisch, wirtschaftlich messbar.",
    },
    en: {
      path: "/en/services/performance-marketing/social-media-advertising",
      title: "Social Media Advertising — Paid Social for Performance — Calvarius",
      description:
        "Paid Social Media Marketing for Meta, LinkedIn, TikTok from Karlstadt am Main. Performance discipline, no content playground. Platform-critical, economically measurable.",
    },
  },
  socialMediaMarketing: {
    de: {
      path: "/leistungen/social-media-marketing",
      title: "Social Media Marketing — Organic & Paid integriert — Calvarius",
      description:
        "Methodisches Social Media Marketing für Mittelstand und E-Commerce. Organic und Paid integriert über Meta, TikTok, LinkedIn und Pinterest — kontrolliert, datengeführt.",
    },
    en: {
      path: "/en/services/social-media-marketing",
      title: "Social Media Marketing — Organic & Paid Integrated — Calvarius",
      description:
        "Methodical social media marketing for mid-sized companies and e-commerce. Organic and paid integrated across Meta, TikTok, LinkedIn, and Pinterest — controlled, data-driven.",
    },
  },
  pmLinkedinAds: {
    de: {
      path: "/leistungen/performance-marketing/linkedin-ads",
      title: "LinkedIn Ads Agentur — B2B-Performance mit Disziplin — Calvarius",
      description:
        "LinkedIn Ads Agentur für B2B-Lead-Gen, Enterprise-SaaS und ABM. Pipeline-Steuerung statt Reach-Vanity, CRM-Integration, Conversions API. Audit kostenlos.",
    },
    en: {
      path: "/en/services/performance-marketing/linkedin-ads",
      title: "LinkedIn Ads Agency — B2B Performance with Discipline — Calvarius",
      description:
        "LinkedIn Ads agency for B2B lead gen, enterprise SaaS, and ABM. Pipeline management instead of reach vanity, CRM integration, Conversions API. Free audit.",
    },
  },
  pmTiktokAds: {
    de: {
      path: "/leistungen/performance-marketing/tiktok-ads",
      title: "TikTok Ads Agentur — Visual-Performance mit Creator-Disziplin — Calvarius",
      description:
        "TikTok Ads Agentur für E-Commerce und DTC-Marken. Spark Ads, Creator-Sourcing, Events API — Performance-Steuerung statt View-Vanity. Audit kostenlos.",
    },
    en: {
      path: "/en/services/performance-marketing/tiktok-ads",
      title: "TikTok Ads Agency — Visual Performance with Creator Discipline — Calvarius",
      description:
        "TikTok Ads agency for e-commerce and DTC brands. Spark Ads, creator sourcing, Events API — performance management instead of view vanity. Free audit.",
    },
  },
  pmMicrosoftAds: {
    de: {
      path: "/leistungen/performance-marketing/microsoft-ads",
      title: "Microsoft Ads Agentur — Search-Komplement für DACH — Calvarius",
      description:
        "Microsoft Ads Agentur für B2B, SaaS und B2C-E-Commerce. Bing-Search, LinkedIn-Profil-Targeting, niedrigere CPCs als Google. Audit kostenlos.",
    },
    en: {
      path: "/en/services/performance-marketing/microsoft-ads",
      title: "Microsoft Ads Agency — Search Complement for DACH — Calvarius",
      description:
        "Microsoft Ads agency for B2B, SaaS, and B2C e-commerce. Bing Search, LinkedIn profile targeting, lower CPCs than Google. Free audit available.",
    },
  },
  pmYoutubeDemandGen: {
    de: {
      path: "/leistungen/performance-marketing/youtube-demand-gen",
      title: "YouTube Ads Agentur — Awareness mit Performance-Logik — Calvarius",
      description:
        "YouTube Ads Agentur für B2C-Marken, E-Commerce und SaaS. TrueView, Demand Gen, View-Through-Steuerung, Asset-Pipeline über picazzoo. Audit kostenlos.",
    },
    en: {
      path: "/en/services/performance-marketing/youtube-demand-gen",
      title: "YouTube Ads Agency — Awareness with Performance Logic — Calvarius",
      description:
        "YouTube Ads agency for B2C brands, e-commerce, and SaaS. TrueView, Demand Gen, view-through management, asset pipeline via picazzoo. Free audit available.",
    },
  },
  pmAffiliateMarketing: {
    de: {
      path: "/leistungen/performance-marketing/affiliate-marketing",
      title: "Affiliate-Marketing Agentur — Partner-Steuerung mit Disziplin — Calvarius",
      description:
        "Affiliate-Marketing Agentur für B2C-E-Commerce. Netzwerk-agnostisch, Tracking-Hygiene, aktive Publisher-Steuerung statt Set-and-Forget. Audit kostenlos.",
    },
    en: {
      path: "/en/services/performance-marketing/affiliate-marketing",
      title: "Affiliate Marketing Agency — Partner Steering with Discipline — Calvarius",
      description:
        "Affiliate marketing agency for B2C e-commerce. Network-agnostic, tracking hygiene, active publisher management instead of set-and-forget. Free audit.",
    },
  },
  imprint: {
    de: {
      path: "/impressum",
      title: "Impressum — Calvarius",
      description: "Anbieterkennzeichnung gemäß § 5 TMG der Calvarius GmbH, Karlstadt am Main.",
    },
    en: {
      path: "/en/imprint",
      title: "Imprint — Calvarius",
      description: "Legal notice according to § 5 TMG for Calvarius GmbH, Karlstadt am Main, Germany.",
    },
    noindex: true,
  },
  privacy: {
    de: {
      path: "/datenschutzerklaerung",
      title: "Datenschutzerklärung — Calvarius",
      description:
        "Informationen zur Verarbeitung personenbezogener Daten auf der Calvarius-Website gemäß DSGVO.",
    },
    en: {
      path: "/en/privacy-policy",
      title: "Privacy Policy — Calvarius",
      description:
        "Information on the processing of personal data on the Calvarius website in accordance with GDPR.",
    },
    noindex: true,
  },
  accessibility: {
    de: {
      path: "/barrierefreiheit",
      title: "Barrierefreiheits-Erklärung — Calvarius",
      description:
        "Erklärung zur digitalen Barrierefreiheit der Calvarius-Website nach BFSG und WCAG 2.1.",
    },
    en: {
      path: "/en/accessibility",
      title: "Accessibility Statement — Calvarius",
      description:
        "Statement on the digital accessibility of the Calvarius website per BFSG and WCAG 2.1.",
    },
    noindex: true,
  },
  regions: {
    de: {
      path: "/regionen",
      title: "Regionen — Performance-Marketing-Agentur in DACH — Calvarius",
      description:
        "Performance-Marketing-Agentur aus Karlstadt am Main. Persönlich vor Ort in Würzburg, Schweinfurt, Aschaffenburg, Frankfurt, Nürnberg — bundesweit digital.",
    },
    en: {
      path: "/en/regions",
      title: "Regions — Performance Marketing Agency in DACH — Calvarius",
      description:
        "Performance marketing agency from Karlstadt am Main. In person in Würzburg, Schweinfurt, Aschaffenburg, Frankfurt, Nürnberg — nationwide digital.",
    },
  },
  "region:wuerzburg": {
    de: {
      path: "/regionen/wuerzburg",
      title: "Performance-Marketing-Agentur Würzburg — KMU-Schwerpunkt — Calvarius",
      description:
        "Performance-Marketing-Agentur in Würzburg-Nähe. KMU-Schwerpunkt im E-Commerce, B2B und SaaS. Aus Karlstadt am Main, 25 km entfernt. Audit kostenlos.",
    },
    en: {
      path: "/en/regions/wuerzburg",
      title: "Performance Marketing Agency Würzburg — SME Focus — Calvarius",
      description:
        "Performance marketing agency near Würzburg. SME focus in e-commerce, B2B, and SaaS. From Karlstadt am Main, 25 km away. Free audit.",
    },
  },
  "region:wuerzburg:seo": {
    de: {
      path: "/regionen/wuerzburg/seo-agentur",
      title: "SEO-Agentur Würzburg — wirtschaftlich priorisiert — Calvarius",
      description:
        "SEO-Agentur für Würzburg und Unterfranken: klassisches SEO plus AI-SEO für ChatGPT, Perplexity & Claude. Wirtschaftlich priorisiert statt Ranking-Vanity. Aus Karlstadt, 25 km entfernt.",
    },
    en: {
      path: "/regionen/wuerzburg/seo-agentur",
      title: "SEO-Agentur Würzburg — wirtschaftlich priorisiert — Calvarius",
      description:
        "SEO-Agentur für Würzburg und Unterfranken: klassisches SEO plus AI-SEO für ChatGPT, Perplexity & Claude. Wirtschaftlich priorisiert statt Ranking-Vanity. Aus Karlstadt, 25 km entfernt.",
    },
  },

  "region:frankfurt": {
    de: {
      path: "/regionen/frankfurt",
      title: "Performance-Marketing-Agentur Frankfurt — Enterprise & Mittelstand — Calvarius",
      description:
        "Performance-Marketing-Agentur für Frankfurt am Main. Finanz, Beratung, B2B-SaaS, internationale Mittelständler. 75 Min. Anfahrt aus Karlstadt am Main.",
    },
    en: {
      path: "/en/regions/frankfurt",
      title: "Performance Marketing Agency Frankfurt — Enterprise & Mid-Market — Calvarius",
      description:
        "Performance marketing agency for Frankfurt am Main. Finance, consulting, B2B SaaS, international mid-market. 75 min from Karlstadt am Main.",
    },
  },
  "region:aschaffenburg": {
    de: {
      path: "/regionen/aschaffenburg",
      title: "Performance-Marketing-Agentur Aschaffenburg — Mittelstand & Industrie — Calvarius",
      description:
        "Performance-Marketing-Agentur für Aschaffenburg und Bayerischer Untermain. Mittelstand, Industrie-Zulieferer, Handel. 50 km Anfahrt. GF operativ.",
    },
    en: {
      path: "/en/regions/aschaffenburg",
      title: "Performance Marketing Agency Aschaffenburg — Mid-Market & Industry — Calvarius",
      description:
        "Performance marketing agency for Aschaffenburg and Bavarian Untermain. Mid-market, industrial suppliers, trade. 50 km drive. MD on the account.",
    },
  },
  "region:schweinfurt": {
    de: {
      path: "/regionen/schweinfurt",
      title: "Performance-Marketing-Agentur Schweinfurt — Industrie & B2B — Calvarius",
      description:
        "Performance-Marketing-Agentur für Schweinfurt: Industrie-Cluster, Maschinenbau, B2B-Zulieferer. 40 km Anfahrt aus Karlstadt am Main. Audit kostenlos.",
    },
    en: {
      path: "/en/regions/schweinfurt",
      title: "Performance Marketing Agency Schweinfurt — Industry & B2B — Calvarius",
      description:
        "Performance marketing agency for Schweinfurt: industrial cluster, machinery, B2B suppliers. 40 km from Karlstadt am Main. Free audit.",
    },
  },
  "region:nuernberg": {
    de: {
      path: "/regionen/nuernberg",
      title: "Performance-Marketing-Agentur Nürnberg — Industrie & E-Commerce — Calvarius",
      description:
        "Performance-Marketing-Agentur für Nürnberg: B2B-Industrie, Konsumgüter, Mittelstands-E-Commerce. Anfahrt 90 Min. per Bahn oder Auto. GF operativ.",
    },
    en: {
      path: "/en/regions/nuernberg",
      title: "Performance Marketing Agency Nürnberg — Industry & E-Commerce — Calvarius",
      description:
        "Performance marketing agency for Nürnberg: B2B industry, consumer goods, mid-market e-commerce. 90 min by train or car. MD on the account. Free audit.",
    },
  },
  blog: {
    de: {
      path: "/blog",
      title: "Blog — Substanz zu Performance, SEO und Stack-Disziplin — Calvarius",
      description:
        "Editorial-substantielle Beiträge zu Performance-Marketing, SEO, Conversion-Optimierung, Tracking und Stack-Disziplin. Aus der Mandate-Praxis von Calvarius.",
    },
    en: {
      path: "/en/blog",
      title: "Blog — Substance on Performance, SEO and Stack Discipline — Calvarius",
      description:
        "Editorial-substantial articles on performance marketing, SEO, conversion optimization, tracking and stack discipline. From Calvarius mandate practice.",
    },
  },
  "blog:lovable-seo-tanstack": {
    de: {
      path: "/blog/lovable-seo-tanstack",
      title: "Lovable SEO-Problem & TanStack-Migration — Calvarius",
      description:
        "Lovable hatte ein strukturelles SEO-Problem mit Vite-SPA. Die TanStack-Umstellung löst es — aber nur für neue Projekte. Diagnose, Lösungsweg, Migrations-Tiefe.",
    },
    en: {
      path: "/en/blog/lovable-seo-tanstack",
      title: "Lovable SEO Problem & TanStack Migration — Calvarius",
      description:
        "Lovable had a structural SEO problem with Vite SPA. The TanStack switch solves it — but only for new projects. Diagnosis, solution path, migration depth.",
    },
  },
  glossary: {
    de: {
      path: "/glossar",
      title: "Glossar — Performance Marketing, SEO und AI-SEO — Calvarius",
      description:
        "Fach-Glossar mit Begriffs-Definitionen aus Performance Marketing, SEO, AI-SEO und Conversion-Optimierung — mit Mandate-Praxis-Substanz statt Wikipedia-Kopie.",
    },
    en: {
      path: "/en/glossary",
      title: "Glossary — Performance Marketing, SEO, AI SEO — Calvarius",
      description:
        "Specialist glossary with term definitions from performance marketing, SEO, AI SEO, and conversion optimization — with mandate-practice substance, not Wikipedia copies.",
    },
  },
} satisfies Record<string, LocalizedSeo>;

export type PageKey = keyof typeof pages;

export const OG_IMAGE_PATH = "/og/default-1200x630.png";
export const OG_IMAGE_URL = `${SITE_URL}${OG_IMAGE_PATH}`;
export const OG_IMAGE_ALT = "Calvarius Corporate Creations Logo";
export const OG_IMAGE_TYPE = "image/png";
export const OG_IMAGE_WIDTH = "1200";
export const OG_IMAGE_HEIGHT = "630";
